import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Download, ChevronRight, Calendar, Search, SlidersHorizontal,
  ArrowUpDown, ChevronLeft, Globe, BarChart3, Shield, Users, Clock,
  Plus, X, Loader2, CalendarClock, BookOpen
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useUserProfile } from '@/lib/useUserProfile';
import { NavBar } from '@/components/NavBar';

// ── Badge colours per report type category ──
const TYPE_BADGES = {
  weekly_summary: { label: 'Weekly summary', color: 'bg-emerald-600 text-white' },
  port_freight_briefing: { label: 'Port & freight', color: 'bg-emerald-600 text-white' },
  country_deep_dive: { label: 'Country deep dive', color: 'bg-[#0D3B4F] text-white' },
  monthly_footprint: { label: 'Monthly briefing', color: 'bg-[#0D3B4F] text-white' },
  sector_report: { label: 'Sector report', color: 'bg-[#00707C] text-white' },
  audience_report: { label: 'Audience report', color: 'bg-amber-600 text-white' },
  compliance: { label: 'Compliance / DFAT', color: 'bg-red-700 text-white' },
  diversification: { label: 'Diversification', color: 'bg-amber-600 text-white' },
  modern_slavery: { label: 'Modern slavery', color: 'bg-red-700 text-white' },
  custom: { label: 'Custom', color: 'bg-[#4E6974] text-white' },
};

const REPORT_TYPE_OPTIONS = [
  { value: 'all', label: 'All report types' },
  { value: 'weekly_summaries', label: 'Weekly summaries' },
  { value: 'country_deep_dives', label: 'Country deep dives' },
  { value: 'sector_reports', label: 'Sector reports' },
  { value: 'audience_specific', label: 'Audience-specific' },
  { value: 'compliance_dfat', label: 'Compliance / DFAT' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All audiences' },
  { value: 'importer', label: 'Supply chain' },
  { value: 'government', label: 'Procurement' },
  { value: 'defence', label: 'Risk' },
  { value: 'all', label: 'Executive' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last quarter' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last year' },
];

const SORT_OPTIONS = [
  { value: 'date_newest', label: 'Date (newest first)' },
  { value: 'date_oldest', label: 'Date (oldest first)' },
  { value: 'country_az', label: 'Country (A-Z)' },
  { value: 'report_type', label: 'Report type' },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const { audienceType } = useUserProfile();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [countryGroups, setCountryGroups] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 20, total_pages: 1 });
  const [filters, setFilters] = useState({ report_type: '', country: '', audience: '', date_range: '', search: '', sort: 'date_newest' });
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateType, setGenerateType] = useState('country_deep_dive');
  const [generateCountry, setGenerateCountry] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  // Fetch reports
  const fetchReports = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filters.report_type) params.report_type = filters.report_type;
      if (filters.country) params.country = filters.country;
      if (filters.audience) params.audience = filters.audience;
      if (filters.date_range) params.date_range = filters.date_range;
      if (filters.search) params.search = filters.search;
      if (filters.sort) params.sort = filters.sort;
      const res = await api.getReports(params);
      const data = res.data;
      setReports(data.reports || []);
      setPagination({ total: data.total, page: data.page, page_size: data.page_size, total_pages: data.total_pages });
    } catch (e) {
      console.error('Error fetching reports:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchReports(1); }, [fetchReports]);

  // Fetch stats, schedule, countries on mount
  useEffect(() => {
    api.getReportStats().then(r => setStats(r.data)).catch(() => {});
    api.getReportSchedule().then(r => setSchedule(r.data)).catch(() => {});
    api.getReportCountries().then(r => setCountryGroups(r.data)).catch(() => {});
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const handlePageChange = (newPage) => {
    fetchReports(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = { report_type: generateType };
      if (generateType === 'country_deep_dive' && generateCountry) {
        payload.country_code = generateCountry;
      }
      await api.generateReport(payload);
      toast.success('Report generated successfully');
      setGenerateOpen(false);
      setGenerateCountry('');
      // Refresh
      fetchReports(1);
      api.getReportStats().then(r => setStats(r.data)).catch(() => {});
    } catch (e) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (report, e) => {
    e.stopPropagation();
    const url = api.downloadReportPdf(report.id);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.title.replace(/\s+/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Report PDF downloaded');
  };

  const getTypeBadge = (type) => TYPE_BADGES[type] || { label: type?.replace(/_/g, ' ') || 'Report', color: 'bg-[#4E6974] text-white' };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">

          {/* ── Page heading ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
            <h1 className="text-3xl font-bold text-[#0D3B4F]" style={{ fontFamily: 'Trebuchet MS, sans-serif' }} data-testid="reports-title">
              Risk reports
            </h1>
            <p className="text-[#4E6974] mt-1">AI-generated risk reports for countries, sectors, and weekly summaries</p>
          </motion.div>

          {/* ── Intro panel ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="bg-[#FAFAFA] rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.04)] border border-[#E0E0E0] mb-6" data-testid="reports-intro-panel">
              <CardContent className="p-6 space-y-3 text-sm text-[#4E6974] leading-relaxed">
                <p>
                  TradePath generates four types of intelligence reports to support your supply chain decision-making.
                  Weekly risk summaries give you a pulse check on conditions every Monday morning.
                  Quarterly country deep dives provide 8-12 page assessments covering risk scores, trade analysis, sector breakdowns,
                  port intelligence, scenario planning, and prioritised recommendations for each of the 42 countries we monitor.
                  Monthly trade footprint briefings track what changed in the specific countries you trade with,
                  so you only read what matters to your business.
                  And custom reports let you combine any countries and sectors into a tailored analysis on demand.
                </p>
                <p>
                  Every report is generated by TradePath's AI analysis engine, drawing from twelve live data sources including
                  geopolitical event monitoring, bilateral trade statistics, freight and port data, economic indicators,
                  and sanctions screening. Risk scores are calculated using a weighted model across eight dimensions and
                  refreshed every two hours. Reports are written in plain English with a focus on what the data means for
                  Australian businesses, not just what the numbers say.
                </p>
                <p>
                  Use the filters below to find reports by type, country, or date range. Quarterly deep dives and monthly
                  briefings are generated automatically on schedule. You can also generate an on-demand deep dive for any
                  country at any time using the button below. All reports can be viewed in your browser or downloaded as
                  branded PDFs ready to share with your team or board.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Stat cards + Generate button ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-stretch">
              <StatCard icon={Globe} label="Countries monitored" value={stats?.countries_monitored ?? '42'} testId="stat-countries" />
              <StatCard icon={BarChart3} label="Reports this quarter" value={stats?.reports_this_quarter ?? '-'} testId="stat-quarter" />
              <StatCard icon={CalendarClock} label="Next quarterly deep dive" value={stats?.next_quarterly_deep_dive ?? '-'} testId="stat-next-dive" />
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => setGenerateOpen(true)}
                  className="bg-[#00707C] hover:bg-[#005F6A] text-white h-full min-h-[80px] px-6 rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
                  data-testid="generate-deep-dive-btn"
                >
                  <Plus className="h-5 w-5 mr-2" /> Generate country deep dive
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowSchedule(!showSchedule)}
                  className="text-[#0097A7] border-[#0097A7] hover:bg-[#0097A7]/5 text-xs"
                  data-testid="toggle-schedule-btn"
                >
                  <Clock className="h-3.5 w-3.5 mr-1" /> {showSchedule ? 'Hide schedule' : 'Report schedule'}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* ── Report schedule panel (collapsible) ── */}
          <AnimatePresence>
            {showSchedule && schedule && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 overflow-hidden">
                <Card className="border border-[#E0E0E0] rounded-lg shadow-sm" data-testid="report-schedule-panel">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold text-[#0D3B4F] mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#0097A7]" /> Report schedule</h3>
                    <div className="space-y-2">
                      {schedule.schedule_items?.map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-[#F5F5F5] rounded px-3 py-2">
                          <span className="text-[#0D3B4F] font-medium">{item.type}</span>
                          <span className="text-[#4E6974]">{item.frequency}</span>
                          <span className="text-[#0097A7] font-medium">Next: {item.next_due}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Search bar ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4E6974]" />
              <Input
                placeholder="Search reports by title, country, or content..."
                value={filters.search}
                onChange={handleSearch}
                className="pl-10 bg-white border-[#E0E0E0] h-10"
                data-testid="report-search-input"
              />
            </div>
          </motion.div>

          {/* ── Filter bar ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
            <Card className="bg-white rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.04)] border-none">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-3">
                  {/* Report type */}
                  <div className="flex-1">
                    <Select value={filters.report_type || 'all'} onValueChange={(v) => handleFilterChange('report_type', v)}>
                      <SelectTrigger data-testid="report-type-filter"><SelectValue placeholder="All report types" /></SelectTrigger>
                      <SelectContent>
                        {REPORT_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Audience */}
                  <div className="flex-1">
                    <Select value={filters.audience || 'all'} onValueChange={(v) => handleFilterChange('audience', v)}>
                      <SelectTrigger data-testid="audience-filter"><SelectValue placeholder="All audiences" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All audiences</SelectItem>
                        <SelectItem value="importer">Supply chain</SelectItem>
                        <SelectItem value="government">Procurement</SelectItem>
                        <SelectItem value="defence">Risk</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Country (grouped) */}
                  <div className="flex-1">
                    <Select value={filters.country || 'all'} onValueChange={(v) => handleFilterChange('country', v)}>
                      <SelectTrigger data-testid="country-filter"><SelectValue placeholder="All countries" /></SelectTrigger>
                      <SelectContent className="max-h-[320px]">
                        <SelectItem value="all">All countries</SelectItem>
                        {countryGroups.map(group => (
                          <SelectGroup key={group.region}>
                            <SelectLabel className="text-xs font-semibold text-[#0097A7] uppercase tracking-wider">{group.region}</SelectLabel>
                            {group.countries.map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Date range */}
                  <div className="flex-1">
                    <Select value={filters.date_range || 'all'} onValueChange={(v) => handleFilterChange('date_range', v)}>
                      <SelectTrigger data-testid="date-range-filter"><SelectValue placeholder="All time" /></SelectTrigger>
                      <SelectContent>
                        {DATE_RANGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Sort */}
                  <div className="flex-1">
                    <Select value={filters.sort} onValueChange={(v) => handleFilterChange('sort', v)}>
                      <SelectTrigger data-testid="sort-filter"><SelectValue placeholder="Sort by" /></SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Results count ── */}
          {!loading && (
            <div className="flex items-center justify-between mb-4 text-xs text-[#4E6974]" data-testid="results-count">
              <span>Showing {reports.length} of {pagination.total} reports (page {pagination.page} of {pagination.total_pages})</span>
            </div>
          )}

          {/* ── Reports list ── */}
          {loading ? (
            <div className="text-center py-16 text-[#4E6974]" data-testid="reports-loading">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-[#0097A7]" />
              Loading reports...
            </div>
          ) : reports.length > 0 ? (
            <div className="space-y-3">
              {reports.map((report, index) => {
                const badge = getTypeBadge(report.report_type);
                return (
                  <motion.div key={report.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.02 }}>
                    <Card
                      className="bg-white rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all cursor-pointer group"
                      onClick={() => navigate(`/reports/${report.id}`)}
                      data-testid={`report-card-${index}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="bg-[#F5F5F5] rounded-lg p-3 hidden sm:block">
                            <FileText className="h-7 w-7 text-[#0097A7]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <Badge className={`${badge.color} text-xs`}>{badge.label}</Badge>
                              {report.on_demand && <Badge variant="outline" className="text-[10px] border-[#0097A7] text-[#0097A7]">On-demand</Badge>}
                              <span className="flex items-center gap-1 text-[11px] text-[#4E6974]">
                                <Calendar className="h-3 w-3" />
                                {formatDate(report.created_at)}
                              </span>
                            </div>
                            <h3 className="text-base font-bold text-[#0D3B4F] mb-1.5 group-hover:text-[#0097A7] transition-colors line-clamp-1">
                              {report.title}
                            </h3>
                            <p className="text-xs text-[#4E6974] line-clamp-2 mb-2">{report.executive_summary}</p>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {report.countries_covered?.slice(0, 6).map(code => (
                                <Badge key={code} variant="secondary" className="bg-[#F0F4F5] text-[#4E6974] text-[10px] px-1.5 py-0">{code}</Badge>
                              ))}
                              {report.countries_covered?.length > 6 && (
                                <Badge variant="secondary" className="bg-[#F0F4F5] text-[#4E6974] text-[10px] px-1.5 py-0">+{report.countries_covered.length - 6}</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleDownload(report, e)}
                              className="text-[#0097A7] border-[#0097A7] hover:bg-[#00707C] hover:text-white text-xs"
                              data-testid={`download-report-${index}`}
                            >
                              <Download className="h-3.5 w-3.5 mr-1" /> PDF
                            </Button>
                            <Button variant="ghost" size="sm" className="text-[#0097A7] text-xs">
                              View <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="reports-empty">
              <BookOpen className="h-12 w-12 text-[#B0BEC5] mx-auto mb-4" />
              <p className="text-[#4E6974] mb-1">No reports found{filters.report_type || filters.country ? ' for this filter combination' : ''}</p>
              <p className="text-xs text-[#B0BEC5] mb-4">Try adjusting your filters or generate a new report</p>
              <Button onClick={() => setGenerateOpen(true)} className="bg-[#00707C] hover:bg-[#005F6A] text-white text-xs" data-testid="empty-generate-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Generate one now
              </Button>
            </div>
          )}

          {/* ── Pagination ── */}
          {pagination.total_pages > 1 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(pagination.page - 1)}
                className="text-xs"
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
              </Button>
              {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
                let pageNum;
                if (pagination.total_pages <= 7) {
                  pageNum = i + 1;
                } else if (pagination.page <= 4) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.total_pages - 3) {
                  pageNum = pagination.total_pages - 6 + i;
                } else {
                  pageNum = pagination.page - 3 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === pagination.page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className={`text-xs w-8 h-8 p-0 ${pageNum === pagination.page ? 'bg-[#00707C] text-white' : ''}`}
                    data-testid={`pagination-page-${pageNum}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => handlePageChange(pagination.page + 1)}
                className="text-xs"
                data-testid="pagination-next"
              >
                Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </motion.div>
          )}

        </div>
      </main>

      {/* ── Generate report dialog ── */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0D3B4F]">Generate report</DialogTitle>
            <DialogDescription className="text-[#4E6974]">
              Select the report type and country to generate an on-demand report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium text-[#0D3B4F] mb-1.5 block">Report type</label>
              <Select value={generateType} onValueChange={setGenerateType}>
                <SelectTrigger data-testid="generate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="country_deep_dive">Country deep dive</SelectItem>
                  <SelectItem value="audience_report">Audience briefing</SelectItem>
                  <SelectItem value="compliance">Compliance / DFAT screening</SelectItem>
                  <SelectItem value="custom">Custom report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {generateType === 'country_deep_dive' && (
              <div>
                <label className="text-xs font-medium text-[#0D3B4F] mb-1.5 block">Country</label>
                <Select value={generateCountry} onValueChange={setGenerateCountry}>
                  <SelectTrigger data-testid="generate-country-select"><SelectValue placeholder="Select a country" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {countryGroups.map(group => (
                      <SelectGroup key={group.region}>
                        <SelectLabel className="text-xs font-semibold text-[#0097A7] uppercase tracking-wider">{group.region}</SelectLabel>
                        {group.countries.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || (generateType === 'country_deep_dive' && !generateCountry)}
              className="bg-[#00707C] hover:bg-[#005F6A] text-white"
              data-testid="generate-confirm-btn"
            >
              {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : 'Generate report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Stat card component ──
const StatCard = ({ icon: Icon, label, value, testId }) => (
  <Card className="flex-1 bg-white rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.04)] border-none min-w-[160px]">
    <CardContent className="p-4 flex items-center gap-3" data-testid={testId}>
      <div className="bg-[#0097A7]/10 rounded-lg p-2.5">
        <Icon className="h-5 w-5 text-[#0097A7]" />
      </div>
      <div>
        <p className="text-[22px] font-bold text-[#0D3B4F] leading-tight">{value}</p>
        <p className="text-[11px] text-[#4E6974]">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default ReportsPage;
